import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDate, toBerlinDateKey } from '@brimo/shared';
import { api } from '../lib/api';
import { sessionQueryKey } from '../lib/session';

/** Types of `GET /notifications` (docs/dev/api-shell.md — Notifications). */

export type NotificationType = 'success' | 'task' | 'mention' | 'notice';
export type NotificationFilter = 'all' | 'unread' | 'mentions';

export interface NotificationItem {
  id: string;
  eventKey: string;
  type: NotificationType;
  title: string;
  text: string | null;
  href: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationPage {
  items: NotificationItem[];
  unread: number;
  nextCursor: string | null;
}

export const notificationsQueryKey = ['notifications'] as const;

export function useNotifications(filter: NotificationFilter, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...notificationsQueryKey, filter],
    queryFn: ({ pageParam, signal }) =>
      api<NotificationPage>(
        `/notifications?filter=${filter}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        { signal },
      ),
    initialPageParam: '',
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
    staleTime: 15_000,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids?: string[]; all?: true }) =>
      api<{ unread: number }>('/notifications/read', { method: 'POST', body }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...notificationsQueryKey] }),
        queryClient.invalidateQueries({ queryKey: sessionQueryKey }),
      ]);
    },
  });
}

export interface DayGroup {
  key: string;
  label: string;
  items: NotificationItem[];
}

/** "Heute", "Gestern", otherwise the date (Europe/Berlin), keeping the server's order. */
export function groupByDay(items: readonly NotificationItem[], now: Date = new Date()): DayGroup[] {
  const today = toBerlinDateKey(now);
  const yesterday = toBerlinDateKey(new Date(now.getTime() - 86_400_000));
  const groups: DayGroup[] = [];
  for (const item of items) {
    const key = toBerlinDateKey(item.createdAt);
    const label = key === today ? 'Heute' : key === yesterday ? 'Gestern' : formatDate(item.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, label, items: [item] });
  }
  return groups;
}
